/**
 * tRPC Client for Auth Portal
 *
 * Type-safe API client using tRPC with React Query integration.
 *
 * @module lib/trpc/client
 */

import { httpBatchLink, loggerLink, type TRPCLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '2-api-gateway/src/trpc/trpc.router';

const isDev = import.meta.env.DEV;

/**
 * Trace headers getter for OpenTelemetry integration
 * Set via setTraceHeadersGetter() from TracingProvider
 */
let getTraceHeaders: (() => Record<string, string>) | null = null;

/**
 * Set the trace headers getter function.
 * Called by TRPCProviderInternal after TracingProvider initializes.
 */
export function setTraceHeadersGetter(getter: () => Record<string, string>): void {
  getTraceHeaders = getter;
}

/**
 * Create tRPC React client with type inference from the API router
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get the API URL from environment (validated by env.ts)
 * No fallback - env.ts will use ports.json in dev, require env var in production
 */
function getBaseUrl(): string {
  // Import env config which has proper dev/prod handling
  // This relies on env.ts validation which already handles ports.json fallback in dev
  return import.meta.env.VITE_API_URL;
}

/**
 * Clear auth tokens from storage
 */
function clearTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

/**
 * Custom error link to handle UNAUTHORIZED errors
 * Redirects to login page when the user's session expires
 */
const errorLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          // Check if this is an UNAUTHORIZED error
          if (err.data?.code === 'UNAUTHORIZED' || err.data?.httpStatus === 401) {
            console.warn('[tRPC] Unauthorized - redirecting to login');

            // Clear stored tokens
            clearTokens();

            // Redirect to login page (within Auth Portal)
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `/login?returnUrl=${returnUrl}`;
          }

          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });
      return unsubscribe;
    });
  };
};

/**
 * Create tRPC client configuration
 */
export function createTRPCClient() {
  return trpc.createClient({
    links: [
      loggerLink({
        enabled: (opts) => isDev || (opts.direction === 'down' && opts.result instanceof Error),
      }),
      errorLink,
      httpBatchLink({
        url: `${getBaseUrl()}/trpc`,
        headers() {
          // Auth portal may not have auth tokens during login flow
          // API returns camelCase 'accessToken' - use consistent key across portals
          const token = localStorage.getItem('accessToken');
          const traceHeaders = getTraceHeaders?.() ?? {};
          return {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...traceHeaders,
          };
        },
      }),
    ],
  });
}

export type { AppRouter };
